/**
 * Debug Logger Utility
 * Provides file-based logging capabilities for debugging in Cursor IDE
 * Writes logs to .cursor/debug.log in NDJSON format via HTTP endpoint
 */

export interface DebugLogEntry {
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  message: string;
  context?: Record<string, unknown>;
  stack?: string;
}

class DebugLogger {
  private logs: DebugLogEntry[] = [];
  private maxLogs = 10000; // Keep last 10k logs in memory
  private enabled = true;
  private readonly debugEndpoint = import.meta.env.DEV ? 'http://127.0.0.1:7242/ingest/257b1bfb-4a72-4943-941f-c6d7a8ba2659' : null;
  private sessionId = `session-${Date.now()}`;
  private logCounter = 0;

  /**
   * Enable or disable debug logging
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  /**
   * Check if logging is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Log a debug message
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log('debug', message, context);
  }

  /**
   * Log an info message
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log('info', message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log('warn', message, context);
  }

  /**
   * Log an error message
   */
  error(message: string, error?: Error | unknown, context?: Record<string, unknown>): void {
    const errorContext = {
      ...context,
      ...(error instanceof Error
        ? {
            errorName: error.name,
            errorMessage: error.message,
            stack: error.stack,
          }
        : { error: String(error) }),
    };
    this.log('error', message, errorContext, error instanceof Error ? error.stack : undefined);
  }

  /**
   * Log a fatal error message
   */
  fatal(message: string, error?: Error | unknown, context?: Record<string, unknown>): void {
    const errorContext = {
      ...context,
      ...(error instanceof Error
        ? {
            errorName: error.name,
            errorMessage: error.message,
            stack: error.stack,
          }
        : { error: String(error) }),
    };
    this.log('fatal', message, errorContext, error instanceof Error ? error.stack : undefined);
  }

  /**
   * Internal log method
   */
  private log(
    level: DebugLogEntry['level'],
    message: string,
    context?: Record<string, unknown>,
    stack?: string
  ): void {
    if (!this.enabled) {
      return;
    }

    const entry: DebugLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(context && { context }),
      ...(stack && { stack }),
    };

    // Add to in-memory log
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift(); // Remove oldest entry
    }

    // Write to .cursor/debug.log via HTTP endpoint in NDJSON format
    this.writeToDebugLog(entry);

    // Also log to console for immediate visibility
    const consoleMethod = level === 'error' || level === 'fatal' 
      ? console.error 
      : level === 'warn' 
      ? console.warn 
      : level === 'debug'
      ? console.debug
      : console.log;

    const logPrefix = `[${entry.timestamp}] [${level.toUpperCase()}]`;
    consoleMethod(`${logPrefix} ${message}`, context || '');
    if (stack) {
      consoleMethod('Stack:', stack);
    }
  }

  /**
   * Write log entry to .cursor/debug.log via HTTP endpoint in NDJSON format
   */
  private writeToDebugLog(entry: DebugLogEntry): void {
    if (typeof window === 'undefined' || !this.debugEndpoint) {
      // Not in browser environment or no debug endpoint configured, skip HTTP logging
      return;
    }

    // Get caller location from stack trace if available
    let location = 'unknown';
    try {
      const stack = new Error().stack;
      if (stack) {
        const lines = stack.split('\n');
        // Find the first line that's not from this file
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line && !line.includes('debugLogger.ts') && !line.includes('Error')) {
            const match = line.match(/([^\/]+\.(ts|tsx|js|jsx)):(\d+):(\d+)/);
            if (match) {
              location = `${match[1]}:${match[3]}`;
              break;
            }
          }
        }
      }
    } catch {
      // Ignore stack trace errors
    }

    // Create NDJSON log entry matching Cursor debug format
    const logId = `log_${Date.now()}_${++this.logCounter}`;
    const ndjsonEntry = {
      id: logId,
      timestamp: new Date(entry.timestamp).getTime(),
      location: location,
      message: entry.message,
      data: {
        level: entry.level,
        ...(entry.context && { context: entry.context }),
        ...(entry.stack && { stack: entry.stack }),
      },
      sessionId: this.sessionId,
      runId: 'default',
      hypothesisId: entry.context?.hypothesisId as string | undefined,
    };

    // Send to debug endpoint (fire and forget)
    fetch(this.debugEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ndjsonEntry),
    }).catch(() => {
      // Silently fail if endpoint is not available
      // This allows the app to work even if debug server is not running
    });
  }

  /**
   * Get all logs
   */
  getLogs(level?: DebugLogEntry['level'], limit?: number): DebugLogEntry[] {
    let filtered = this.logs;
    
    if (level) {
      filtered = this.logs.filter(log => log.level === level);
    }

    if (limit) {
      filtered = filtered.slice(-limit);
    }

    return [...filtered];
  }

  /**
   * Get logs since a specific time
   */
  getLogsSince(timestamp: Date | string): DebugLogEntry[] {
    const since = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return this.logs.filter(log => new Date(log.timestamp) >= since);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs to a downloadable file
   * This works in browser environments by creating a download
   */
  exportLogs(format: 'json' | 'text' = 'text'): void {
    if (this.logs.length === 0) {
      console.warn('No logs to export');
      return;
    }

    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(this.logs, null, 2);
      filename = `debug-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    } else {
      // Text format
      content = this.logs
        .map(log => {
          const time = new Date(log.timestamp).toLocaleString();
          let line = `[${time}] [${log.level.toUpperCase()}] ${log.message}`;
          
          if (log.context) {
            line += `\n  Context: ${JSON.stringify(log.context, null, 2)}`;
          }
          
          if (log.stack) {
            line += `\n  Stack: ${log.stack}`;
          }
          
          return line;
        })
        .join('\n\n');
      
      filename = `debug-${new Date().toISOString().split('T')[0]}.log`;
      mimeType = 'text/plain';
    }

    // Create download link
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Get log statistics
   */
  getStats(): {
    total: number;
    byLevel: Record<string, number>;
    oldest: string | null;
    newest: string | null;
  } {
    const byLevel: Record<string, number> = {
      debug: 0,
      info: 0,
      warn: 0,
      error: 0,
      fatal: 0,
    };

    this.logs.forEach(log => {
      byLevel[log.level] = (byLevel[log.level] || 0) + 1;
    });

    return {
      total: this.logs.length,
      byLevel,
      oldest: this.logs.length > 0 ? this.logs[0].timestamp : null,
      newest: this.logs.length > 0 ? this.logs[this.logs.length - 1].timestamp : null,
    };
  }
}

// Singleton instance
export const debugLogger = new DebugLogger();

// Initialize with a startup message
if (typeof window !== 'undefined') {
  debugLogger.info('Debug logger initialized', {
    environment: import.meta.env.MODE,
    timestamp: new Date().toISOString(),
  });
}

